import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Field, reduxForm } from 'redux-form';
import MenuItem from '@material-ui/core/MenuItem';
import LanguageIcon from '@material-ui/icons/Language';
import IconButton from '@material-ui/core/IconButton';
import LaunchIcon from '@material-ui/icons/Launch';
import classnames from 'classnames';

import ORTypography from 'components/ORTypography/ORTypography';
import WrappedSelectField from 'components/WrappedSelectField/WrappedSelectField';

const propTypes = {
  input: PropTypes.shape({ name: PropTypes.string }),
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  availableCloudProviders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  availableServiceSubtypesNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableRegionsNames: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedRegion: PropTypes.string,
  selectedService: PropTypes.string,
  isDisabled: PropTypes.bool,
  isORv4: PropTypes.bool,
  change: PropTypes.func.isRequired,
  trackButtonClick: PropTypes.func.isRequired,
};

const defaultProps = {
  input: {},
  isDisabled: false,
  isORv4: false,
  selectedRegion: '',
  selectedService: '',
};

const useStyles = makeStyles((theme) => ({
  regionMenuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  regionMenuIcon: {
    position: 'relative',
    marginLeft: theme.spacing(2),
    color: theme.customColors.mediumGrey,
  },
  launchButton: {
    marginLeft: theme.spacing(2),
    backgroundColor: theme.customColors.sky,
    color: theme.customColors.black,
    padding: theme.spacing(1),
    borderRadius: 3,
    boxShadow: `1px 1px 4px ${theme.customColors.black}`,

    '&:hover': {
      backgroundColor: theme.customColors.sky,
    },
  },
  selected: {
    color: theme.customColors.teal,
  },
  privateRegionAsterisk: {
    color: theme.customColors.red,
  },
  cloudMenuItem: {
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  betaText: {
    color: theme.palette.primary.main,
    fontStyle: 'italic',
    fontSize: '0.7857142857rem',
    fontWeight: 'bold',
  },
}));

export function UnstyledFormSelect({
  label,
  className,
  name,
  availableCloudProviders,
  availableServiceSubtypesNames,
  availableRegionsNames,
  selectedRegion,
  selectedService,
  isDisabled,
  isORv4,
  change,
  trackButtonClick,
  // isPrivateRegion,
}) {
  /**
   * As of 2020-04, we have only one service subtype per service.
   * This automatically selects the first service subtype available.
   * If we actually get other subtypes, this can be removed.
   */
  React.useEffect(() => {
    if (availableServiceSubtypesNames.length > 0 && name === 'cloudProvider') {
      change('serviceSubtype', availableServiceSubtypesNames[0]);
    }
  }, [availableServiceSubtypesNames, change, name]);

  const handleCloudClick = () => {
    change('region', undefined);
  };

  const classes = useStyles();

  const menuLabel = (
    <MenuItem key="label" value="" disabled>
      <em>{label}</em>
    </MenuItem>
  );
  const rackspaceCloudLink = isORv4 ? (
    <MenuItem
      key="ORv4-link"
      component="a"
      value="Rackspace SSO"
      href={process.env.REACT_APP_ORV4_SSO_LOGIN}
      target="_blank"
      rel="noopener noreferrer"
    >
      Rackspace Cloud
      <IconButton className={classes.launchButton}>
        <LaunchIcon />
      </IconButton>
    </MenuItem>
  ) : null;

  let cloudList = availableCloudProviders.map((cloudProvider) => (
    <MenuItem
      key={cloudProvider.id}
      value={cloudProvider.name}
      onClick={handleCloudClick}
      className={classes.cloudMenuItem}
    >
      {cloudProvider.name}
      {cloudProvider.maturityLevel === 2 && (
        <ORTypography className={classes.betaText}>BETA</ORTypography>
      )}
    </MenuItem>
  ));

  cloudList = [menuLabel, ...cloudList, rackspaceCloudLink];

  let regionList = availableRegionsNames.map((region) => {
    const isSelected = region.name === selectedRegion;
    return (
      <MenuItem
        key={region.name}
        value={region.name}
        ListItemClasses={{ root: classes.regionMenuItem }}
      >
        {region.organization ? (
          <div>
            {region.name}
            <span className={classes.privateRegionAsterisk}>*</span>
          </div>
        ) : (
          region.name
        )}
        <LanguageIcon
          className={classnames(classes.regionMenuIcon, isSelected ? classes.selected : '')}
        />
      </MenuItem>
    );
  });

  regionList = [menuLabel, ...regionList];

  let typeList = availableServiceSubtypesNames.map((subtype) => (
    <MenuItem key={subtype} value={subtype}>
      {subtype}
    </MenuItem>
  ));

  typeList = [menuLabel, ...typeList];

  const listMap = {
    cloudProvider: cloudList,
    region: regionList,
    serviceSubtype: typeList,
  };
  return (
    <Field
      className={className}
      name={name}
      component={WrappedSelectField}
      label={label}
      disabled={isDisabled}
      onChange={(e) => {
        trackButtonClick(selectedService.concat(' ', e.target.value.toUpperCase()));
      }}
    >
      {listMap[name]}
    </Field>
  );
}

UnstyledFormSelect.propTypes = propTypes;
UnstyledFormSelect.defaultProps = defaultProps;

export default reduxForm({
  form: 'create',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(UnstyledFormSelect);
